import { useEffect, useRef, useState } from "react";

/** Numeric counter that eases from the previous value to the next when the
 * value updates. Non-numeric strings (e.g. CJK decorative chars used while
 * there is no scan data) bypass animation and render as-is. */
export function AnimatedNumber({
  value,
  durationMs = 700,
}: {
  value: string;
  durationMs?: number;
}) {
  const parsed = Number(value);
  const isNumeric = value.trim() !== "" && Number.isFinite(parsed);
  if (!isNumeric) return <>{value}</>;
  return <AnimatedCounter target={parsed} durationMs={durationMs} />;
}

function AnimatedCounter({
  target,
  durationMs,
}: {
  target: number;
  durationMs: number;
}) {
  const [display, setDisplay] = useState(0);
  const lastShownRef = useRef(0);

  useEffect(() => {
    const from = lastShownRef.current;
    if (from === target) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic — slows down as it approaches target.
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.round(from + (target - from) * eased);
      lastShownRef.current = current;
      setDisplay(current);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return <>{display}</>;
}
